/**
 * CreateCharacterModal component - modal for creating a new character.
 */

import { useState, useEffect } from 'react';
import { router } from '@inertiajs/react';
import type { CharacterClassWithRanges } from '@/types/characters';
import { ClassSelector } from './ClassSelector';

interface CreateCharacterModalProps {
    /** The hex ID where the character will be created */
    hexId: number;
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback to close the modal */
    onClose: () => void;
    /** Callback when character is created successfully */
    onCreated: () => void;
}

/**
 * Modal for creating a new primary character (Celestial Moth).
 * Fetches available classes and allows selection before creation.
 */
export function CreateCharacterModal({
    hexId,
    isOpen,
    onClose,
    onCreated,
}: CreateCharacterModalProps) {
    const [classes, setClasses] = useState<CharacterClassWithRanges[]>([]);
    const [statLabels, setStatLabels] = useState<Record<string, string>>({});
    const [skillLabels, setSkillLabels] = useState<Record<string, string>>({});
    const [selectedClass, setSelectedClass] = useState<string | null>(null);
    const [characterName, setCharacterName] = useState('');
    const [pronouns, setPronouns] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch available classes when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const fetchClasses = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`/hex/${hexId}/characters/classes`);
                if (!response.ok) throw new Error('Failed to fetch classes');
                const data = await response.json();
                setClasses(data.classes);
                setStatLabels(data.statLabels);
                setSkillLabels(data.skillLabels);
            } catch (err) {
                setError('Failed to load character classes');
                console.error('Failed to fetch classes:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchClasses();
    }, [isOpen, hexId]);

    // Handle character creation
    const handleCreate = () => {
        if (!selectedClass || !characterName.trim() || isCreating) return;

        setIsCreating(true);
        setError(null);

        router.post(
            `/hex/${hexId}/characters`,
            {
                name: characterName.trim(),
                pronouns: pronouns.trim() || null,
                class: selectedClass,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    onCreated();
                    onClose();
                    // Reset form
                    setSelectedClass(null);
                    setCharacterName('');
                    setPronouns('');
                },
                onError: (errors) => {
                    setError(Object.values(errors).flat().join(', '));
                },
                onFinish: () => {
                    setIsCreating(false);
                },
            }
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden border border-slate-700 flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-700">
                    <h2 className="text-xl font-bold text-white">Create Your Celestial Moth</h2>
                    <p className="text-sm text-gray-400 mt-1">
                        Your Celestial Moth is your primary character. Choose a class and name wisely - these cannot be changed later.
                    </p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {isLoading ? (
                        <div className="text-center text-gray-400 py-8">
                            Loading character classes...
                        </div>
                    ) : error && classes.length === 0 ? (
                        <div className="text-center text-red-400 py-8">{error}</div>
                    ) : (
                        <div className="space-y-6">
                            {/* Character Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Character Name
                                </label>
                                <input
                                    type="text"
                                    value={characterName}
                                    onChange={(e) => setCharacterName(e.target.value)}
                                    placeholder="Enter your character's name..."
                                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    maxLength={100}
                                />
                            </div>

                            {/* Pronouns */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Pronouns <span className="text-gray-500">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={pronouns}
                                    onChange={(e) => setPronouns(e.target.value)}
                                    placeholder="e.g., he/him, she/her, they/them..."
                                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    maxLength={50}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Used when describing this character's actions in adventures.
                                </p>
                            </div>

                            {/* Class Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Choose Your Class
                                </label>
                                <ClassSelector
                                    classes={classes}
                                    selectedClass={selectedClass}
                                    onSelect={setSelectedClass}
                                    statLabels={statLabels}
                                    skillLabels={skillLabels}
                                />
                            </div>

                            {/* Error display */}
                            {error && (
                                <div className="p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300 text-sm">
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isCreating}
                        className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={!selectedClass || !characterName.trim() || isCreating || isLoading}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                        {isCreating ? 'Creating...' : 'Create Character'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateCharacterModal;

